"use client";

import { useIsMobile } from "@/hooks/use-mobile";
import { type BreadcrumbItem } from "@/utils/breadcrumb-utils";
import { ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { ClickableBreadcrumb, EllipsisBreadcrumb, NavigationBreadcrumb } from "./breadcrumb-components";

interface BreadcrumbsProps {
	items: BreadcrumbItem[];
	className?: string;
	onBreadcrumbClick?: (item: BreadcrumbItem, index: number) => void | Promise<void>;
	disabled?: boolean;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
	items,
	className = "",
	onBreadcrumbClick,
	disabled = false,
}) => {
	// SSR-safe mobile detection using media query hook
	const isMobile = useIsMobile();
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	// Always render full items during SSR, then adjust on client
	const displayItems =
		isClient && isMobile && items.length > 3
			? [items[0], { label: "...", variant: "default" as const }, ...items.slice(-2)]
			: items;

	// Handle breadcrumb click
	const handleBreadcrumbClick = async (item: BreadcrumbItem, index: number) => {
		if (onBreadcrumbClick) {
			await onBreadcrumbClick(item, index);
		}
	};

	// Render breadcrumb item - SINGLE RENDERING PATH
	const renderBreadcrumbItem = (item: BreadcrumbItem, index: number) => {
		const isLastItem = index === displayItems.length - 1;
		const BreadCrumbComp = item.href && !isLastItem ? NavigationBreadcrumb : ClickableBreadcrumb;

		// Ellipsis
		if (item.label === "...") {
			return <EllipsisBreadcrumb isMobile={isMobile} />;
		}

		// ALL breadcrumbs go through the same clickable component
		// Handler decides behavior (navigation, filtering, or disabled)
		return (
			<BreadCrumbComp
				item={item}
				isMobile={isMobile}
				onClick={() => handleBreadcrumbClick(item, index)}
				disabled={disabled || isLastItem}
			/>
		);
	};

	return (
		<div className={`${className}`}>
			<nav
				className={`flex items-center ${isClient && isMobile ? "gap-0.5" : "gap-1"} text-sm`}
				aria-label="Breadcrumb"
			>
				<ol className={`flex items-center ${isClient && isMobile ? "gap-0.5" : "gap-1"}`}>
					{displayItems.map((item, index) => (
						<li key={index} className={`flex items-center ${isClient && isMobile ? "gap-0.5" : "gap-1"}`}>
							{index > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
							{renderBreadcrumbItem(item, index)}
						</li>
					))}
				</ol>
			</nav>
		</div>
	);
};

export default Breadcrumbs;
